import * as THREE from 'three';
import { LightmapLoader } from './lightmap-loader';
import type { GraphicsQuality } from '../core/settings';

// ---------------------------------------------------------------------------
// Baked Lightmap System
// ---------------------------------------------------------------------------
// Applies pre-baked lightmap textures to scene meshes. Supports:
//   1. External lightmap files (KTX2 or image) loaded from disk
//   2. Auto-detection of lightmaps already embedded in glTF scenes
//
// Lightmaps coexist with CSM dynamic shadows — CSM provides real-time
// character/prop shadows while lightmaps add static global illumination,
// bounce light, and soft ambient occlusion.
//
// Quality presets control lightmap intensity:
//   low=0.7  medium=0.85  high=1.0  ultra=1.2
// Even on 'low', lightmaps are sampled (essentially free on the GPU —
// just a texture lookup) but at reduced intensity.
// ---------------------------------------------------------------------------

/** Configuration for a single lightmap to apply to the scene. */
export interface LightmapConfig {
  /** Path to the lightmap texture (KTX2 or standard image format). */
  path: string;
  /** Intensity multiplier (1.0 = neutral). */
  intensity: number;
  /** Optional filter — when provided, only meshes passing the predicate receive this lightmap. Null/omitted means all static meshes. */
  meshFilter?: (mesh: THREE.Mesh) => boolean;
}

/** Per-mesh record so we can restore base intensity on quality change. */
interface AppliedEntry {
  mesh: THREE.Mesh;
  baseIntensity: number;
}

/** Intensity multiplier per quality preset. */
const QUALITY_MULTIPLIER: Record<GraphicsQuality, number> = {
  low: 0.7,
  medium: 0.85,
  high: 1.0,
  ultra: 1.2,
  'mobile-low': 0.5,
  'mobile-medium': 0.65,
  'mobile-high': 0.8,
};

export class LightmapSystem {
  private readonly loader: LightmapLoader;
  private readonly applied: AppliedEntry[] = [];
  private quality: GraphicsQuality;

  constructor(renderer: THREE.WebGLRenderer, quality: GraphicsQuality) {
    this.loader = new LightmapLoader(renderer);
    this.quality = quality;
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  External lightmap application
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Load and apply one or more lightmap configurations to a scene graph.
   *
   * Each config specifies a texture path, a base intensity, and an optional
   * mesh filter. The lightmap is assigned to `material.lightMap` on every
   * qualifying MeshStandardMaterial in the subtree.
   */
  async applyToScene(scene: THREE.Object3D, configs: LightmapConfig[]): Promise<void> {
    for (const config of configs) {
      const lightmap = await this.loader.loadLightmap(config.path);

      scene.traverse((obj: THREE.Object3D) => {
        if (!(obj instanceof THREE.Mesh)) return;
        if (config.meshFilter && !config.meshFilter(obj)) return;

        const mat = obj.material as THREE.MeshStandardMaterial;
        if (!mat.isMeshStandardMaterial) return;

        // Ensure UV2 attribute exists (copy from UV1 if missing)
        this.ensureUV2(obj);

        // Apply lightmap with quality-adjusted intensity
        mat.lightMap = lightmap;
        mat.lightMapIntensity = config.intensity * QUALITY_MULTIPLIER[this.quality];
        mat.needsUpdate = true;

        this.applied.push({ mesh: obj, baseIntensity: config.intensity });
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  glTF auto-detection
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Auto-detect and scale lightmaps already embedded in a glTF scene.
   *
   * The glTF loader maps TEXCOORD_1 to `geometry.attributes.uv2` and
   * assigns `material.lightMap` automatically when the glTF extension
   * `KHR_materials_lightmap` (or equivalent) is present. This method
   * adjusts the intensity to match the current quality preset.
   */
  applyFromGLTF(scene: THREE.Object3D): void {
    scene.traverse((obj: THREE.Object3D) => {
      if (!(obj instanceof THREE.Mesh)) return;

      const geo = obj.geometry;
      const mat = obj.material as THREE.MeshStandardMaterial;

      // Only process meshes that already have a lightmap assigned by the glTF loader
      if (geo.attributes.uv2 && mat.lightMap) {
        const baseIntensity = mat.lightMapIntensity;
        mat.lightMapIntensity = baseIntensity * QUALITY_MULTIPLIER[this.quality];
        mat.needsUpdate = true;

        this.applied.push({ mesh: obj, baseIntensity });
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  Quality control
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Update lightmap intensity on all applied meshes when the graphics
   * quality preset changes.
   */
  setQuality(quality: GraphicsQuality): void {
    if (this.quality === quality) return;
    this.quality = quality;

    // H38: Prune stale/disposed mesh references before iterating
    this.pruneDisposed();

    const multiplier = QUALITY_MULTIPLIER[quality];
    for (const entry of this.applied) {
      const mat = entry.mesh.material as THREE.MeshStandardMaterial;
      if (mat.lightMap) {
        mat.lightMapIntensity = entry.baseIntensity * multiplier;
        mat.needsUpdate = true;
      }
    }
  }

  /**
   * Remove entries whose meshes have been removed from the scene graph.
   * Prevents the applied array from growing unboundedly with stale references.
   */
  private pruneDisposed(): void {
    for (let i = this.applied.length - 1; i >= 0; i--) {
      const mesh = this.applied[i].mesh;
      // A mesh removed from the scene graph has no parent
      if (!mesh.parent) {
        this.applied.splice(i, 1);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  Helpers
  // ─────────────────────────────────────────────────────────────────────────

  /** Ensure the mesh has a uv2 attribute. Copies from uv if missing. */
  private ensureUV2(mesh: THREE.Mesh): void {
    const geo = mesh.geometry;
    if (!geo.attributes.uv2 && geo.attributes.uv) {
      geo.setAttribute('uv2', geo.attributes.uv.clone());
    }
  }

  /** Expose the loader for external lightmap-existence checks. */
  getLoader(): LightmapLoader {
    return this.loader;
  }

  /** Clean up all lightmaps and restore materials. */
  dispose(): void {
    for (const entry of this.applied) {
      const mat = entry.mesh.material as THREE.MeshStandardMaterial;
      if (mat.isMeshStandardMaterial) {
        mat.lightMap = null;
        mat.needsUpdate = true;
      }
    }
    this.applied.length = 0;
    this.loader.dispose();
  }
}
